import type { ExtractedField, SourceKind } from "./useExtractionStore";

export interface MergeResult {
  /** Subset of ``incomingValues`` that survives the DXF-wins rule.
   * The form should ``setValue`` only on these keys so a DXF-stamped
   * field isn't overwritten by a voice memo. */
  valuesToWrite: Record<string, unknown>;
  /** Merged confidence map to write back to the store. Engineering
   * stamps (dxf/ifc) are preserved verbatim; NL stamps fill the gaps. */
  mergedConfidence: Record<string, ExtractedField>;
  /** Effective source for the merged store entry — used by
   * ``setExtraction`` so the next merge decision uses the right
   * priority. ``"dxf"`` / ``"ifc"`` if any survives, otherwise ``"nl"``. */
  resultSourceKind: SourceKind;
  /** Fields that were skipped because a DXF/IFC stamp already owned
   * them. Useful for a toast: "Iz glasovne poruke: 8 polja popunjeno,
   * 3 preskočeno (DXF već popunio)." */
  skippedFields: string[];
}

const isEngineering = (k: SourceKind | null | undefined): k is "dxf" | "ifc" =>
  k === "dxf" || k === "ifc";

/**
 * Merge an incoming extraction wave on top of the current store
 * confidence, enforcing the DXF-wins rule when ``incomingSourceKind``
 * is ``"nl"``.
 *
 * Why not just diff at the form-values level?
 *   The form lives in react-hook-form; the confidence stamps live in
 *   Zustand. Both need to stay in sync. Merging here gives us one
 *   place to reason about "did DXF already speak for this field?"
 *   instead of scattering store reads across each prefill caller.
 */
export function mergePrefill(
  currentConfidence: Record<string, ExtractedField>,
  currentSourceKind: SourceKind | null,
  incomingValues: Record<string, unknown>,
  incomingConfidence: Record<string, ExtractedField>,
  incomingSourceKind: SourceKind,
): MergeResult {
  const valuesToWrite: Record<string, unknown> = {};
  const mergedConfidence: Record<string, ExtractedField> = { ...currentConfidence };
  const skippedFields: string[] = [];

  // For each field the incoming wave wants to set: decide.
  const incomingFields = new Set<string>([
    ...Object.keys(incomingValues),
    ...Object.keys(incomingConfidence),
  ]);

  for (const field of incomingFields) {
    const existingStamp = currentConfidence[field];
    const existingIsEngineering =
      existingStamp != null && isEngineering(currentSourceKind);

    if (incomingSourceKind === "nl" && existingIsEngineering) {
      skippedFields.push(field);
      continue;
    }

    if (field in incomingValues) {
      valuesToWrite[field] = incomingValues[field];
    }
    if (field in incomingConfidence) {
      mergedConfidence[field] = incomingConfidence[field];
    }
  }

  // Decide the source kind to stamp on the store. If the incoming wave
  // is engineering-grade, it dominates. Otherwise keep whatever was
  // dominating before (so "DXF then NL then NL" still shows DXF as
  // owning the original DXF fields).
  let resultSourceKind: SourceKind;
  if (isEngineering(incomingSourceKind)) {
    resultSourceKind = incomingSourceKind;
  } else if (isEngineering(currentSourceKind)) {
    resultSourceKind = currentSourceKind;
  } else {
    resultSourceKind = "nl";
  }

  return {
    valuesToWrite,
    mergedConfidence,
    resultSourceKind,
    skippedFields,
  };
}

/**
 * Frontend safety cap: NL-extracted numeric values are forced to
 * confidence ≤ 0.69 (the threshold below which ``ConfidenceStamp``
 * paints the gold "provjeri" badge). Even if the LLM is overconfident
 * about "120 m²", the architect will see a mandatory-review stamp.
 *
 * Why backend doesn't do this:
 *   The backend faithfully reports what the model returned. The trust
 *   discount lives in the UI layer so the raw signal stays inspectable
 *   in logs/metrics.
 */
export function capNumericConfidenceForNL(
  extracted: Record<string, ExtractedField>,
  numericFieldNames: readonly string[],
): Record<string, ExtractedField> {
  const NUMERIC_CAP = 0.69;
  const out: Record<string, ExtractedField> = {};
  for (const [field, payload] of Object.entries(extracted)) {
    if (numericFieldNames.includes(field) && payload.confidence > NUMERIC_CAP) {
      out[field] = { ...payload, confidence: NUMERIC_CAP };
    } else {
      out[field] = payload;
    }
  }
  return out;
}
